import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Button, Form, Input } from "antd";
import ReCAPTCHA from "react-google-recaptcha";
import {
  checkEmail,
  checkPhone,
  forgotPasswordEmail,
  forgotPasswordPhone,
  verificationRequestEmail,
  verificationRequestPhone,
} from "../../api";
import SetPassword from "../../components/common/SetPassword";
import { CHECK_IDENTIFIER, VERIFICATION } from "../../constants";
import EnterOtp from "../../components/common/EnterOtp";
import { useHistory } from "react-router-dom";
import { commonUtil } from "../../utils";
import { ValidationForm } from "../../common/interfaces";
import styles from "./forgotPassword.module.css";
import PhoneNotVerifyPopup from "../../components/common/PhoneNotVerifyPopup";
import { convertPhone } from "../../utils/common";

interface Props {}

const ForgotPassword: React.FC<Props> = (props: Props) => {
  const { t } = useTranslation(["common", "notification"]);
  const history = useHistory();
  const [emailOrPhone, setEmailOrPhone] = useState<string>("");
  const [validateEmailOrPhone, setValidateEmailOrPhone] =
    useState<ValidationForm | null>(null);
  const [validateCaptcha, setValidateCaptcha] = useState<ValidationForm | null>(
    null
  );
  const [otpType, setOtpType] = useState<"email" | "phone">("email");
  const [verificationId, setVerificationId] = useState<number>(0);
  const [popupToShow, setPopupToShow] = useState<
    "ENTER_OTP" | "SET_PASSWORD" | "NOTIFY_PHONE" | null
  >(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [phoneNotVerify, setPhoneNotVerify] = useState<{
    phone: string;
    emails: string[];
  }>();
  const recaptchaRef: any = React.createRef();

  const handleChangeEmailOrPhone = (value: string) => {
    setEmailOrPhone(value.trim());
    setValidateEmailOrPhone(null);
    setValidateCaptcha(null);
  };

  const handleSubmit = async () => {
    if (!emailOrPhone) {
      setValidateEmailOrPhone({
        status: "error",
        text: t("notification:Vui lòng nhập email hoặc số điện thoại"),
      });
      return false;
    }
    if (
      !commonUtil.isEmailValid(emailOrPhone) &&
      !commonUtil.isPhoneValid(emailOrPhone)
    ) {
      setValidateEmailOrPhone({
        status: "error",
        text: t("notification:Email hoặc số điện thoại không đúng định dạng"),
      });
      return false;
    }

    // @ts-ignore
    const token = await recaptchaRef.current.getValue();
    // @ts-ignore
    recaptchaRef.current.reset();
    setValidateCaptcha(null);

    if (!token || token == "") {
      setValidateCaptcha({
        status: "error",
        text: t("notification:Vui lòng chọn Tôi không phải người máy"),
      });
      return false;
    }

    setIsLoading(true);
    const res = commonUtil.isEmailValid(emailOrPhone)
      ? await verificationRequestEmail({
          feature: VERIFICATION.FEATURE.FORGOT_PASSWORD,
          email: emailOrPhone,
          token: token,
        })
      : await verificationRequestPhone({
          feature: VERIFICATION.FEATURE.FORGOT_PASSWORD,
          phone: commonUtil.convertPhone(emailOrPhone),
          token: token,
        });
    setIsLoading(false);
    if (res.status === "error") {
      commonUtil.showMessage("error", res.message);
    } else {
      setVerificationId(res.data.verificationId);
      setOtpType(commonUtil.isEmailValid(emailOrPhone) ? "email" : "phone");
      setPopupToShow("ENTER_OTP");
    }
  };

  const onConfirmOtpSuccess = async () => {
    if (commonUtil.isPhoneValid(emailOrPhone)) {
      const resCheckPhone = await checkPhone({
        phone: convertPhone(emailOrPhone),
      });
      if (resCheckPhone.status === "success") {
        if (resCheckPhone.data.status === CHECK_IDENTIFIER.STATUS.NOT_VERIFY) {
          if (resCheckPhone.data.emails.length) {
            setPhoneNotVerify({
              phone: emailOrPhone,
              emails: resCheckPhone.data.emails,
            });
            setPopupToShow("NOTIFY_PHONE");
            setIsLoading(false);
          }
        } else if (
          resCheckPhone.data.status === CHECK_IDENTIFIER.STATUS.VERIFIED
        ) {
          setPopupToShow("SET_PASSWORD");
        } else if (
          resCheckPhone.data.status === CHECK_IDENTIFIER.STATUS.NOT_EXIST
        ) {
          commonUtil.showMessage("error", "Số điện thoại chưa tồn tại");
        }
      }
    } else if (commonUtil.isEmailValid(emailOrPhone)) {
      const resCheckEmail = await checkEmail({ email: emailOrPhone });
      if (resCheckEmail.status === "success") {
        if (resCheckEmail.data.status === CHECK_IDENTIFIER.STATUS.NOT_VERIFY) {
          commonUtil.showMessage("error", "Email chưa tồn tại");
        } else if (
          resCheckEmail.data.status === CHECK_IDENTIFIER.STATUS.VERIFIED
        ) {
          setPopupToShow("SET_PASSWORD");
        } else if (
          resCheckEmail.data.status === CHECK_IDENTIFIER.STATUS.NOT_EXIST
        ) {
          commonUtil.showMessage("error", "Email chưa tồn tại");
        }
      }
    }
  };

  const onSubmitForgotPassword = async (password: string) => {
    const res =
      otpType === "email"
        ? await forgotPasswordEmail({
            email: emailOrPhone,
            password,
            verificationId,
          })
        : await forgotPasswordPhone({
            phone: commonUtil.convertPhone(emailOrPhone),
            password,
            verificationId,
          });
    if (res.status === "error") {
      commonUtil.showMessage("error", res.message);
      return;
    }
    commonUtil.showMessage(
      "success",
      t("notification:Thay đổi mật khẩu thành công")
    );
    history.push(`/login${window.location.search}`);
    return;
  };

  return (
    <div className={"container mx-auto mt-16 md:mt-40 mb-16 md:mb-40 main"}>
      <div className="bg-white m-auto p-16 max-w-[472px] rounded-sm overflow-hidden">
        <Form className={styles.formForgotPassword} size="large">
          <p className="text-2xl font-semibold mb-36">
            {t("common:Lấy lại mật khẩu")}
          </p>
          <p className="mb-8">
            {t(
              "common:Nhập địa chỉ email hoặc số điện thoại của bạn và chúng tôi sẽ gửi cho bạn mã xác nhận để lấy lại mật khẩu"
            )}
          </p>
          <Form.Item
            validateStatus={validateEmailOrPhone?.status}
            help={validateEmailOrPhone?.text}
          >
            <Input
              type="text"
              placeholder={t("common:Email hoặc số điện thoại")}
              value={emailOrPhone}
              onChange={(event) => {
                handleChangeEmailOrPhone(event.target.value);
              }}
              onPressEnter={handleSubmit}
            />
          </Form.Item>
          <Form.Item
            className="mb-24"
            validateStatus={validateCaptcha?.status}
            help={validateCaptcha?.text}
          >
            <ReCAPTCHA
              className="w-full"
              ref={recaptchaRef}
              sitekey="6Ld_n7EZAAAAAMif3OxoZ1kvUvyBN3uXTXMTz4w7"
            />
          </Form.Item>
          <Button
            className={styles.continueBtn}
            type="primary"
            block={true}
            onClick={handleSubmit}
            loading={isLoading}
          >
            {t("common:Tiếp tục")}
          </Button>
        </Form>
      </div>
      {popupToShow === "ENTER_OTP" ? (
        <EnterOtp
          verificationId={verificationId}
          type={otpType}
          feature={VERIFICATION.FEATURE.FORGOT_PASSWORD}
          value={
            otpType === "email"
              ? emailOrPhone
              : commonUtil.convertPhone(emailOrPhone)
          }
          onClose={() => {
            setPopupToShow(null);
          }}
          onResend={(verificationId) => {
            setVerificationId(verificationId);
          }}
          onConfirmSuccess={onConfirmOtpSuccess}
        />
      ) : null}
      {popupToShow === "SET_PASSWORD" ? (
        <SetPassword
          titleText="Thay đổi mật khẩu"
          confirmText="Thay đổi"
          onClose={() => {
            setPopupToShow(null);
          }}
          onSubmit={onSubmitForgotPassword}
        />
      ) : null}
      {phoneNotVerify && popupToShow === "NOTIFY_PHONE" ? (
        <PhoneNotVerifyPopup
          isRegister={false}
          phone={phoneNotVerify.phone}
          emails={phoneNotVerify.emails}
          onCancel={() => {
            setPopupToShow(null);
            setPhoneNotVerify(undefined);
          }}
        />
      ) : null}
    </div>
  );
};
export default ForgotPassword;
